import React, { useState } from 'react'
import moment from 'moment'
import { FieldArray } from 'formik'
import { useMediaQuery } from 'react-responsive'
import _ from 'lodash'

import {
    Row,
    Col,
    Card,
    H1,
    H5,
    H6,
    FormikMultiSelect,
    FormikSimpleSelect,
    FormikCheckbox,
    FormikInput,
    FormikTextArea,
    Button,
    DeleteButton,
    Dialog,
    FormikRadioGroup,
    Divider,
    ButtonGroup,
    ButtonMenu,
    NavButtonLinkMenuItem,
    Switch,
    FormikDate,
    ButtonLink
} from '../../../Elements'
import { Classes } from '@blueprintjs/core'

import { PebbleDimensionsForm, PebbleScheduleForm, PebbleWorkDetailsForm, PebbleNotesForm } from './PebbleForms'
import { GuniteDimensionsForm, GuniteScheduleForm, GuniteWorkDetailsForm, GuniteNotesForm } from './GuniteForms'
import { WarrantyJobDetailsForm, WarrantyJobForm, WarrantyWorkDetailsForm } from './WarrantyForms'

import { uniqBy } from 'lodash'
import PresiteForm from './PresiteForm'
import { filterCrewNamesBySubdivision } from '../../../utils/helpers'

const cardStyle = {
    //minWidth: 300
}

const dividerStyle = {
    marginBottom: '20px'
}


const DimensionsForm = ({ values, valuePrefix }) => {
    return <>
        {values.job_category && values.job_category.id === 1 && <GuniteDimensionsForm values={values} valuePrefix={valuePrefix} />}
        {values.job_category && values.job_category.id === 2 && <PebbleDimensionsForm values={values} valuePrefix={valuePrefix} />}
    </>
}

const ScheduleForm = ({ values, valuePrefix, filterJobStatuses, setFieldValue }) => {
    return <>
        {values.job_category && values.job_category.id === 1 && <GuniteScheduleForm valuePrefix={valuePrefix} />}
        {values.job_category && values.job_category.id === 2 && <PebbleScheduleForm valuePrefix={valuePrefix}
            filterJobStatuses={filterJobStatuses} values={values} currentStatus={values?.status} setFieldValue={setFieldValue} />}
    </>
}


const DetailForm = ({ jobId, values, valuePrefix, jobTypes, poolTypes, spaTypes, filterJobStatuses, isPebble, isGunite, isWarranty, showJobTypeDependents,
    jobCategories, jobPriorities, jobDescriptions, showPresite, setShowPresite, allJobStatuses }) => {
    const showJobType = values && values.job_category
    const showPoolType = showJobTypeDependents && !isWarranty //add list of types that should show pool
    const showSpaType = showJobTypeDependents && !isWarranty // add list of types that should show spa
    const getWorkOrderMenuItems = (targetDetails, targetWorkOrders, is_boulder, is_daily, is_prep) => (
        values[targetDetails] && values[targetDetails][targetWorkOrders] && values[targetDetails][targetWorkOrders].length > 0
            ? values[targetDetails][targetWorkOrders].map(w => {
                let displayText
                let workOrderType = 'boulder' //default
                if (targetDetails === 'warranty_details') {
                    displayText = w.date_completed ? `${w.date_completed} ` : `(No Date Completed) `
                    workOrderType = 'repair'
                } else {
                    displayText = w.date ? `${w.date} ` : `(No Date) `
                }
                if (!is_boulder) {
                    if (targetDetails === 'gunite_details') {
                        displayText = displayText.concat((w.crew && w.crew.name) ? `- ${w.crew.name}` : `- (No Crew)`)
                        workOrderType = 'gunite'
                    } else if (targetDetails === 'pebble_details') {
                        workOrderType = is_daily
                            ? 'daily'
                            : is_prep ?
                                'prep'
                                : 'pebble'
                    }
                    if (targetDetails === 'pebble_details' && (targetWorkOrders === 'work_order_ids' || targetWorkOrders === 'prep_work_order_ids')) {
                        displayText = displayText.concat((w.foreman && w.foreman.name) ? `- ${w.foreman.name}` : `- (No Foreman)`)
                    }
                }

                let navLink = `/job/${jobId}/${values.id}/work-order/${workOrderType}/${w.id}/edit`
                if (is_prep) {
                    navLink = `/job/${jobId}/${values.id}/prep-work-order/${w.id}/pdf`
                }

                return <NavButtonLinkMenuItem
                    inNewTab
                    text={displayText}
                    style={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    to={navLink}
                />
            })
            : <></>
    )

    const filterJobTypes = () => values.job_category && values.job_category.id
        ? jobTypes.filter(t => (isGunite && t.show_gunite) || (isPebble && t.show_pebble) || (isWarranty && t.show_warranty))
        : []

    const filteredJobDescriptions = () => {
        const selectedJobTypes = (values.job_types && values.job_types.map(j => { return j.id })) || []
        const mappedJobDescriptions = jobDescriptions
            ? jobDescriptions.map(jd => ({
                id: jd.job_description.id,
                name: jd.job_description.name,
                job_type_id: jd.job_type.id
            }))
            : []
        const matchedJobDescriptions = mappedJobDescriptions.filter(jtjd => selectedJobTypes.includes(jtjd.job_type_id)) || []
        return uniqBy(matchedJobDescriptions, 'id')
    }

    const findNewDescriptionValue = (newTypes) => {
        const selectedJobTypes = (newTypes && newTypes.map(j => j.id)) || []
        const isStillValid = values.warranty_details &&
            values.warranty_details.description &&
            values.warranty_details.description.job_type_id &&
            selectedJobTypes.some(t => t === values.warranty_details.description.job_type_id)

        return isStillValid
            ? values.warranty_details.description
            : null
    }

    return (
        <>
            <H5 style={{ display: 'inline-block' }}>Details</H5>
            {values.job_category && !isWarranty && values?.job_types?.length > 0 && <Switch
                innerLabel='Show Pre-Site'
                onChange={() => setShowPresite(!showPresite)}
                style={{ float: 'right' }}
            />}
            <Row>
                <Col lg={6} style={{ alignSelf: 'center' }}>
                    {!values?.id
                        ? <FormikRadioGroup
                            label='Category'
                            items={jobCategories}
                            key={`${valuePrefix}.job_category`}
                            name={`${valuePrefix}.job_category`}
                            dependantNames={[`${valuePrefix}.job_types`]}
                            sideEffects={[
                                { conditional: (selectedVal) => selectedVal?.id === 2, target: `${valuePrefix}.status`, newValueCalculator: () => allJobStatuses.find(s => s.id === 2) },
                                { conditional: (selectedVal) => selectedVal?.id !== 2, target: `${valuePrefix}.status`, newValueCalculator: () => null }
                            ]} //set to pending if they select pebble, or null if not pebble
                        />
                        : <H1 style={{ textAlign: 'center' }}>{values.job_category?.name}</H1>
                    }
                </Col>
                <Col lg={6}>
                    {isWarranty &&
                        <FormikInput
                            name={`${valuePrefix}.warranty_details.job_entered_by`}
                            label='Job Entered By'
                        />
                    }
                    {showJobType &&
                        <FormikMultiSelect
                            name={`${valuePrefix}.job_types`}
                            label='Job Type'
                            items={filterJobTypes()}
                            listenerComponents={[
                                { componentName: `${valuePrefix}.warranty_details.description`, componentNewValueCalculator: (a, b, c) => findNewDescriptionValue(b) }
                            ]}
                            disableMaxListSize
                        />}
                    {!isPebble && showJobTypeDependents &&
                        <FormikSimpleSelect
                            name={`${valuePrefix}.status`}
                            label='Job Status'
                            items={filterJobStatuses()}
                            nullable
                            listenerComponents={[
                                {
                                    componentName: `${valuePrefix}.awaiting_builder_date`,
                                    componentNewValueCalculator: (a, b, c) => {
                                        return b?.id !== 19 ?
                                            c.awaiting_builder_date = null
                                            : c.awaiting_builder_date = moment().format('YYYY-MM-DD')
                                    },
                                    supportingProps: {
                                        awaiting_builder_date: values.awaiting_builder_date
                                    }
                                },
                                {
                                    componentName: `${valuePrefix}.gunite_details.finished_date`, componentNewValueCalculator: (a, b, c) => {
                                        if (b && b.id && b.id === 1 && (values.gunite_details.finished_date == null || values.gunite_details.finished_date === '')) {
                                            //Find non-boulder, flash or other gunite history entry with the most recent date
                                            const mostRecentEntry = _.maxBy(c.gunite_history_entries?.filter(e => e.type?.id < 9
                                                && e.gunite_date !== null & e.gunite_date !== ''), (e) => { return e.gunite_date })
                                            return mostRecentEntry?.gunite_date

                                        } else {
                                            return values.gunite_details?.finished_date
                                        }
                                    }, supportingProps: {
                                        gunite_history_entries: values.gunite_details?.gunite_history
                                    }
                                },
                                {
                                    componentName: `${valuePrefix}.gunite_details.flash_date`, componentNewValueCalculator: (a, b, c) => {
                                        if (b && b.id && b.id === 1 && (values.gunite_details.flash_date == null || values.gunite_details.flash_date === '')) {
                                            //Find most recent flash entry
                                            const mostRecentEntry = _.maxBy(c.gunite_history_entries?.filter(e => [9].includes(e.type?.id)
                                                && e.gunite_date !== null & e.gunite_date !== ''), (e) => { return e.gunite_date })
                                            return mostRecentEntry?.gunite_date

                                        } else {
                                            return values.gunite_details?.flash_date
                                        }
                                    }, supportingProps: {
                                        gunite_history_entries: values.gunite_details?.gunite_history
                                    }
                                },
                            ]}
                        />
                    }
                </Col>
            </Row>
            <Row>
                <Col lg={6}>
                    {showPoolType &&
                        <FormikSimpleSelect
                            nullable
                            name={`${valuePrefix}.pool_type`}
                            label='Pool Type'
                            items={poolTypes}
                        />}
                    {showJobTypeDependents && isGunite &&
                        <FormikDate
                            name={`${valuePrefix}.gunite_details.finished_date`}
                            label='Finished Date'
                        />
                    }
                </Col>
                <Col lg={6}>
                    {showSpaType &&
                        <FormikSimpleSelect
                            nullable
                            name={`${valuePrefix}.spa_type`}
                            label='Spa Type'
                            items={spaTypes}
                        />
                    }
                    {showJobTypeDependents && isGunite && values.status?.id === 19 &&
                        <FormikDate
                            name={`${valuePrefix}.awaiting_builder_date`}
                            label='Awaiting Builder'
                        />
                    }
                </Col>
            </Row>
            {showJobTypeDependents &&
                <>
                    <Divider style={dividerStyle} />
                    {isWarranty &&
                        <>
                            <Row>
                                <Col xs={5} sm={5} md={5} lg={6} style={{ paddingTop: '1vh' }}>
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_cash_on_delivery`}
                                        label='COD'
                                        checked={values && values.is_cash_on_delivery}
                                    />
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_critical_note`}
                                        label='Critical Note'
                                        checked={values?.is_critical_note}
                                    />
                                    {values?.is_critical_note &&
                                        <FormikTextArea
                                            name={`${valuePrefix}.critical_note`}
                                        />
                                    }

                                    {values.warranty_details.imported_job_code &&
                                        <H6 style={{ textAlign: 'right' }}>Fleetmatics Job Code: {values.warranty_details.imported_job_code}</H6>
                                    }
                                </Col>
                                <Col lg={6}>
                                    <ButtonGroup>
                                        <ButtonLink
                                            to={`/job/${jobId}/${values.id}/work-order/new/repair`}
                                            target='_blank'
                                            text='Repair Work Order'
                                            disabled={!values.id}
                                            style={{ marginLeft: 0 }}
                                        />
                                        <ButtonMenu menuIcon='caret-down' buttonStyle={{ margin: '0' }} children={getWorkOrderMenuItems('warranty_details', 'work_order_ids', false, false)}
                                            disabled={!values.id || !values.warranty_details || !values.warranty_details.work_order_ids || values.warranty_details.work_order_ids.length < 1} />
                                    </ButtonGroup>
                                </Col>
                            </Row>
                            <Divider style={dividerStyle} />
                        </>
                    }
                    {!isWarranty &&
                        <>
                            <Row>
                                <Col lg={6}>
                                    {isPebble &&
                                        <>
                                            <FormikCheckbox
                                                name={`${valuePrefix}.pebble_details.is_request_form`}
                                                label='Request Form'
                                                checked={values && values.pebble_details && values.pebble_details.is_request_form}
                                            />
                                            <FormikCheckbox
                                                name={`${valuePrefix}.pebble_details.is_can_show`}
                                                label='Can Show'
                                                checked={values && values.pebble_details && values.pebble_details.is_can_show}
                                            />
                                            <FormikCheckbox
                                                name={`${valuePrefix}.pebble_details.is_no_prep_print`}
                                                label='No Prep'
                                                checked={values && values.pebble_details && values.pebble_details.is_no_prep_print}
                                            />
                                            <FormikCheckbox
                                                name={`${valuePrefix}.pebble_details.is_trash_pick_up`}
                                                label='Trash Pick Up'
                                                checked={values && values.pebble_details && values.pebble_details.is_trash_pick_up}
                                            />
                                        </>}
                                    {isGunite &&
                                        <>
                                            <FormikCheckbox
                                                name={`${valuePrefix}.gunite_details.is_spa_negative_edge`}
                                                label='Spa has Negative Edge'
                                                checked={values && values.gunite_details && values.gunite_details.is_spa_negative_edge}
                                            />
                                            <FormikCheckbox
                                                name={`${valuePrefix}.gunite_details.is_spa_rolled_edge`}
                                                label='Spa has Rolled Edge'
                                                checked={values && values.gunite_details && values.gunite_details.is_spa_rolled_edge}
                                            />
                                            <FormikCheckbox
                                                name={`${valuePrefix}.gunite_details.is_flash`}
                                                label='Flash'
                                                checked={values && values.gunite_details && values.gunite_details.is_flash}
                                            />
                                        </>}
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_invoiced`}
                                        label='Invoiced'
                                        checked={values && values.is_invoiced}
                                    />

                                </Col>
                                <Col lg={6}>
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_in_print_queue`}
                                        label='In Print Queue'
                                        checked={values && values.is_in_print_queue}
                                    />
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_cash_on_delivery`}
                                        label='COD'
                                        checked={values && values.is_cash_on_delivery}
                                    />
                                    <FormikCheckbox
                                        name={`${valuePrefix}.is_critical_note`}
                                        label='Critical Note'
                                        checked={values?.is_critical_note}
                                    />
                                    {values?.is_critical_note &&
                                        <FormikTextArea
                                            name={`${valuePrefix}.critical_note`}
                                        />
                                    }
                                    {isPebble &&
                                        <>
                                            <FormikCheckbox
                                                name={`${valuePrefix}.pebble_details.is_slurry_pickup`}
                                                label='Slurry Pickup'
                                                checked={values && values.pebble_details && values.pebble_details.is_slurry_pickup}
                                            />
                                            {values?.pebble_details?.is_slurry_pickup &&
                                                <FormikTextArea
                                                    name={`${valuePrefix}.pebble_details.slurry_pickup_notes`}
                                                    label="Slurry Pickup Notes"
                                                />
                                            }
                                        </>}
                                </Col>
                            </Row>
                            <Divider style={dividerStyle} />
                        </>
                    }
                    {isWarranty &&
                        <WarrantyJobDetailsForm
                            values={values}
                            valuePrefix={valuePrefix}
                            jobPriorities={jobPriorities}
                            jobDescriptions={filteredJobDescriptions()}
                        />
                    }
                    <Row style={{ paddingLeft: '8px' }}>
                        {isGunite && <>
                            <ButtonGroup>
                                <ButtonLink
                                    to={`/job/${jobId}/${values.id}/work-order/new/gunite`}
                                    target='_blank'
                                    text='Gunite Work Order'
                                    disabled={!values.id}
                                />
                                <ButtonMenu menuIcon='caret-down' buttonStyle={{ margin: '0' }} children={getWorkOrderMenuItems('gunite_details', 'work_order_ids', false, false)}
                                    disabled={!values.id || !values.gunite_details || !values.gunite_details.work_order_ids || values.gunite_details.work_order_ids.length < 1} />
                            </ButtonGroup>

                            <Button onClick={() => window.open(`/job/${jobId}/gunite/${values.id}`, "_blank")} icon='label' disabled={!values.id}>Gunite</Button>
                            <ButtonGroup>
                                <ButtonLink
                                    to={`/job/${jobId}/${values.id}/work-order/new/boulder`}
                                    target='_blank'
                                    text='Boulder Work Order'
                                    disabled={!values.id}
                                />
                                <ButtonMenu menuIcon='caret-down' buttonStyle={{ margin: '0' }} children={getWorkOrderMenuItems('gunite_details', 'boulder_work_order_ids', true, false)}
                                    disabled={!values.id || !values.gunite_details || !values.gunite_details.boulder_work_order_ids || values.gunite_details.boulder_work_order_ids.length < 1} />
                            </ButtonGroup>
                        </>}
                        {isPebble && <>
                            <ButtonGroup>
                                <ButtonLink
                                    to={`/job/${jobId}/${values.id}/work-order/new/pebble`}
                                    target='_blank'
                                    text='Pebble Work Order'
                                    disabled={!values.id}
                                />
                                <ButtonMenu menuIcon='caret-down' buttonStyle={{ margin: '0' }} children={getWorkOrderMenuItems('pebble_details', 'work_order_ids', false, false)}
                                    disabled={!values.id || !values.pebble_details || !values.pebble_details.work_order_ids || values.pebble_details.work_order_ids.length < 1} />
                            </ButtonGroup>
                            <ButtonGroup>
                                <ButtonLink
                                    to={`/job/${jobId}/${values.id}/work-order/new/daily`}
                                    target='_blank'
                                    text='Detail Work Order'
                                    disabled={!values.id}
                                />
                                <ButtonMenu menuIcon='caret-down' buttonStyle={{ margin: '0' }} children={getWorkOrderMenuItems('pebble_details', 'daily_work_order_ids', false, true)}
                                    disabled={!values.id || !values.pebble_details || !values.pebble_details.daily_work_order_ids || values.pebble_details.daily_work_order_ids.length < 1} />
                            </ButtonGroup>
                            <ButtonMenu
                                rightMenuIcon={'caret-down'}
                                menuText={'Prep'}
                                children={getWorkOrderMenuItems('pebble_details', 'prep_work_order_ids', false, false, true)}
                                disabled={!values.id || values?.pebble_details?.prep_work_order_ids?.length < 1}
                            />
                            <Button onClick={() => window.open(`/job/${jobId}/pebble/${values.id}`, "_blank")} icon='label'>Pebble</Button>
                            <Button onClick={() => window.open(`/job/${jobId}/prep/${values.id}`, "_blank")} icon='label'>Prep</Button>
                        </>}
                    </Row>
                </>}
        </>
    )
}

const WorkDetailsForm = ({
    values,
    valuePrefix,
    jobId,
    pebbleColorAdditives,
    pebbleColors,
    pebbleSummaryDetails,
    colors,
    pebblePhases,
    pebbleColorMaterialTypes,
    poolSections,
    crews,
    guniteHistoryTypes,
    employees,
    additions,
    jobStatuses,
    companyDivisions,
    jobOrders,
    setFieldValue,
    slurryPickupOptions,
    ...props
}) => {
    return <>
        {values?.job_category?.id === 1 && <GuniteWorkDetailsForm
            jobId={jobId}
            crews={filterCrewNamesBySubdivision(crews, 'gunite')}
            guniteHistoryTypes={guniteHistoryTypes}
            jobOrders={jobOrders}
            values={values}
            additions={additions}
            jobStatuses={jobStatuses}
            valuePrefix={valuePrefix}
            setFieldValue={setFieldValue}
        />}
        {values?.job_category?.id === 2 && <PebbleWorkDetailsForm
            {...props}
            crews={crews}
            values={values}
            additions={additions}
            employees={employees}
            jobStatuses={jobStatuses}
            valuePrefix={valuePrefix}
            pebbleColors={pebbleColors}
            poolSections={poolSections}
            pebblePhases={pebblePhases}
            slurryPickupOptions={slurryPickupOptions}
            pebbleColorAdditives={pebbleColorAdditives}
            pebbleColorMaterialTypes={pebbleColorMaterialTypes}
            setFieldValue={setFieldValue}
            jobOrders={jobOrders}
        />}
        {values?.job_category?.id === 3 && <WarrantyWorkDetailsForm
            {...props}
            crews={crews}
            values={values}
            additions={additions}
            employees={employees}
            jobStatuses={jobStatuses}
            valuePrefix={valuePrefix}
            pebbleColors={pebbleColors}
            poolSections={poolSections}
            pebbleColorAdditives={pebbleColorAdditives}
            pebbleSummaryDetails={pebbleSummaryDetails}
            companyDivisions={companyDivisions}
        />}
    </>
}

export const GeneralComments = ({ name, generalComments, jobDetailId, isPresite = false }) => {
    const isMobile = useMediaQuery({ query: '(max-width: 950px)' })
    const textAlign = isMobile ? 'center' : 'left'
    const existingCommentStyle = { background: '#00000014', borderRadius: '5px', textAlign }
    const newCommentStyle = { textAlign }

    const commentAccessor = isPresite ? 'comment' : 'general_comment'

    return <>
        <H5>General Comments</H5>
        <FieldArray
            name={name}
            render={({ push, remove }) => (<>
                {generalComments?.map((gc, idx) => {
                    return (<>
                        {idx > 0 &&
                            <Row>
                                <Col lg={12}>
                                    <Divider />
                                </Col>
                            </Row>
                        }
                        <Row>
                            <Col lg={2} style={{ textAlign: 'center' }}>
                                {gc.is_new
                                    ? <>
                                        <p style={{ margin: '0' }}> --- </p>
                                    </>
                                    : <>
                                        <p style={{ margin: '0' }}>{gc.date_created ? moment(gc.date_created).format('MM/DD/YYYY') : '(No Date)'}</p>
                                        <p style={{ margin: '0' }}>{gc.date_created ? moment(gc.date_created).format('h:mm a') : ''}</p>
                                    </>
                                }
                            </Col>
                            <Divider />
                            <Col lg={9.9} style={gc.is_new ? newCommentStyle : existingCommentStyle}>
                                {gc.is_new
                                    ? <FormikTextArea
                                        key={`${name}-${idx}-${commentAccessor}`}
                                        name={`${name}.${idx}.${commentAccessor}`}
                                        rows={1}
                                        disabled={!gc.is_new}
                                    />
                                    : <p>{gc[commentAccessor]}</p>
                                }
                            </Col>
                        </Row>
                    </>
                    )
                })}
                <Button minimal icon='plus' intent='primary' text='Add' onClick={() => {
                    push(
                        {
                            id: -1,
                            is_new: true,
                            date_created: '---',
                            job_detail_id: jobDetailId
                        })
                }
                } />
            </>
            )}
        />
    </>
}

const NotesForm = ({ valuePrefix, values }) => {
    return <>
        {values.job_category && values.job_category.id === 1 && <GuniteNotesForm
            valuePrefix={valuePrefix}
            values={values}
        />}
        {values.job_category && values.job_category.id === 2 && <PebbleNotesForm
            valuePrefix={valuePrefix}
            values={values}
        />}
    </>
}


export default ({
    jobId,
    values,
    valuePrefix,
    dirty,
    jobTypes,
    poolTypes,
    spaTypes,
    jobStatuses,
    pebbleDetailJobTypes,
    poolSections,
    pebbleColors,
    pebbleColorAdditives,
    pebbleColorMaterialTypes,
    selectedDetailTab,
    onChangeDetailTab,
    crews,
    guniteHistoryTypes,
    employees,
    additions,
    jobCategories,
    warrantyCategories,
    jobPriorities,
    jobDescriptions,
    pebbleSummaryDetails,
    vgbMainDrainsBy,
    slurryTypes,
    setMainTabState,
    documentPresiteTabRefresh,
    setDocumentPresiteTabRefresh,
    repairSupervisors,
    setFieldValue,
    jobOrders,
    job,
    subJobIndex,
    deleteSubJobDialogIsOpen,
    setDeleteSubJobDialogIsOpen,
    onSubJobDelete,
    pebblePhases,
    presiteSupervisors,
    documentTags,
    companyDivisions,
    slurryPickupOptions,
    ...props
}) => {
    const [showPresite, setShowPresite] = useState(false)

    const showJobTypeDependents = values && values.job_types && values.job_types.length > 0

    const isPebble = values.job_category && values.job_category.id === 2
    const isGunite = values.job_category && values.job_category.id === 1
    const isWarranty = values.job_category && values.job_category.id === 3

    const filterJobStatuses = (subStatusKey) => {
        return subStatusKey
            ? jobStatuses.filter(s => s[subStatusKey])
            : values.job_category && values.job_category.id
                ? isGunite
                    ? jobStatuses.filter(s => isGunite && s.show_gunite).map(s => ({
                        ...s, name: s.name === 'Weeping' ? 'Cave In' : s.name
                    }))
                    : jobStatuses.filter(s => (isPebble && s.show_pebble) || (isWarranty && s.show_warranty))
                : []
    }

    const categoryIfDefined = values.job_category ? values.job_category.name : ''
    return (
        <>
            <Dialog
                icon='trash'
                title={`Delete ${categoryIfDefined} Sub-Job?`}
                isOpen={deleteSubJobDialogIsOpen}
                canOutsideClickClose={true}
                canEscapeKeyClose={true}
                isCloseButtonShown={true}
                onClose={() => setDeleteSubJobDialogIsOpen(false)}
            >
                <div className={Classes.DIALOG_BODY}>
                    <p>{`Are you sure you want to delete this ${values.job_category ? values.job_category.name : ''} sub-job? It cannot be reversed`}</p>
                    <DeleteButton onClick={(e) => {
                        onSubJobDelete({ job: job, job_detail_id: values.id, job_detail_index: subJobIndex }, props)
                        setDeleteSubJobDialogIsOpen(false)
                    }} />
                </div>
            </Dialog>
            <Row>
                <Col xs={12} lg={isWarranty ? 5 : 9}>
                    <Row>
                        <Col xs={12} lg={isWarranty ? 12 : 7}>
                            <Card style={{ minWidth: '300px' }}>
                                <DetailForm
                                    jobId={jobId}
                                    values={values}
                                    valuePrefix={valuePrefix}
                                    jobTypes={jobTypes}
                                    poolTypes={poolTypes}
                                    spaTypes={spaTypes}
                                    jobStatuses={jobStatuses}
                                    filterJobStatuses={filterJobStatuses}
                                    isPebble={isPebble}
                                    isGunite={isGunite}
                                    isWarranty={isWarranty}
                                    showJobTypeDependents={showJobTypeDependents}
                                    jobCategories={jobCategories}
                                    jobPriorities={jobPriorities}
                                    jobDescriptions={jobDescriptions}
                                    setShowPresite={setShowPresite}
                                    showPresite={showPresite}
                                    allJobStatuses={jobStatuses}
                                />
                            </Card>
                        </Col>
                        {showJobTypeDependents && !isWarranty &&
                            <Col xs={12} lg={5}>
                                <Card style={cardStyle}>
                                    <DimensionsForm
                                        values={values}
                                        valuePrefix={valuePrefix}
                                    />
                                </Card>
                            </Col>
                        }
                    </Row>
                    <Row>
                        <Col lg={12}>
                            {showJobTypeDependents && !isWarranty && showPresite &&
                                <>
                                    <Row>
                                        <Col xs={12}>
                                            <PresiteForm
                                                slurryTypes={slurryTypes}
                                                vgbMainDrainsBy={vgbMainDrainsBy}
                                                values={values}
                                                valuePrefix={valuePrefix}
                                                jobId={jobId}
                                                setMainTabState={setMainTabState}
                                                documentPresiteTabRefresh={documentPresiteTabRefresh}
                                                setDocumentPresiteTabRefresh={setDocumentPresiteTabRefresh}
                                                presiteSupervisors={presiteSupervisors}
                                                employees={employees}
                                                documentTags={documentTags}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            }
                        </Col>
                    </Row>
                </Col>
                {showJobTypeDependents && !isWarranty &&
                    <Col xs={12} lg={3}>
                        <Card style={cardStyle}>
                            <ScheduleForm
                                values={values}
                                valuePrefix={valuePrefix}
                                filterJobStatuses={filterJobStatuses}
                                setFieldValue={setFieldValue}
                            />
                        </Card>
                    </Col>
                }
                {showJobTypeDependents && isWarranty &&
                    <Col xs={12} lg={7}>
                        <Card style={cardStyle}>
                            <WarrantyJobForm
                                values={values}
                                valuePrefix={valuePrefix}
                                warrantyCategories={warrantyCategories}
                                repairSupervisors={repairSupervisors}
                            />
                        </Card>
                    </Col>
                }
            </Row>
            <Row>
                {showJobTypeDependents &&
                    <Col xs={12} lg={12}>
                        <Card style={cardStyle}>
                            <WorkDetailsForm
                                {...props}
                                jobId={jobId}
                                values={values}
                                valuePrefix={valuePrefix}
                                pebbleColorMaterialTypes={pebbleColorMaterialTypes}
                                pebbleColors={pebbleColors}
                                pebbleColorAdditives={pebbleColorAdditives}
                                poolSections={poolSections}
                                pebblePhases={pebblePhases}
                                crews={crews}
                                guniteHistoryTypes={guniteHistoryTypes}
                                employees={employees}
                                jobStatuses={jobStatuses}
                                additions={additions}
                                pebbleSummaryDetails={pebbleSummaryDetails}
                                setFieldValue={setFieldValue}
                                jobOrders={jobOrders}
                                companyDivisions={companyDivisions}
                                slurryPickupOptions={slurryPickupOptions}
                            />
                        </Card>
                    </Col>}
            </Row>

            <Row>
                {showJobTypeDependents &&
                    <Col lg={12}>
                        <NotesForm
                            values={values}
                            valuePrefix={valuePrefix}
                            crew={crews}
                        />
                    </Col>
                }
            </Row>
        </>
    )
}

